// @ts-nocheck
import {
  Fragment,
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigationStore } from "src/store/navigationStore";

import { useHexagonSize } from "src/hooks/useHexagonSize";

import { interpolateVector3Array } from "src/utils/navigation";
import {
  Vector3Point,
  Point3D,
  filterStraightLinePoints,
} from "src/utils/three";

import { CoordinateUtil } from "src/utils/CoordinateUtil";

import {
  getMapSettingByVenueCode,
  getModelConfigMapByLcMapId,
  findMapAlignWithLcMapId,
  getVenueModelConfigByVenueCode,
  getBaseUrl,
  getAltitudeByFloorName,
} from "src/utils/map-util";

import {
  getFloorNumber,
  getFloorNumberArray,
  getFloorString,
  getInterpolatedNumberArray,
} from "src/const/navigationConst";

import * as pc from "playcanvas";
import {
  vec3Distance,
  hexToRgbArray,
  getYAngleDegree,
  interpolateVector3,
} from "src/utils/game";
import { OrbitControls } from "@playcanvas/react/scripts";
import { useTexture, useMaterial, useAppEvent } from "@playcanvas/react/hooks";
import { Application, Entity } from "@playcanvas/react";
import {
  Render,
  Camera,
  Light,
  Environment,
} from "@playcanvas/react/components";
import { useEnvAtlas } from "@playcanvas/react/hooks";
import { GSplat, Script } from "@playcanvas/react/components";
import { CameraControls } from "playcanvas/scripts/esm/camera-controls.mjs";
import { useApp, useSplat, useModel } from "@playcanvas/react/hooks";
import { Gltf } from "@playcanvas/react";

import {
  GlbModel,
} from "@/components/playcanvas/playcanvasComponents";

export default function NavigationTopView() {
  const SCALE_BASE = 1500;
  const [mapSettingState, setMapSettingState] = useState();
  const mapSettingRef = useRef();
  const latestProgressRef = useRef<number>(0);
  const currentLcMapIdRef = useRef("");
  const targetAltitudeRef = useRef<number>(0);

  const latestNavigationPositionArrayRef = useRef<pc.Vec3[]>([]);

  const latestNavigationCameraPositionRef = useRef<pc.Vec3>(
    new pc.Vec3(0, 0, 0),
  );
  const latestNavigationCameraLookingPositionRef = useRef<pc.Vec3>(
    new pc.Vec3(0, 0, 0),
  );
  const latestNavigationPercentagePositionDictionary = useRef<
    Record<number, number[]>
  >({});

  const { HEX_WIDTH_INNER, HEX_HEIGHT_INNER } = useHexagonSize();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 769);
    };

    checkIsMobile(); // Check on initial render
    window.addEventListener("resize", checkIsMobile);

    // Cleanup listener on component unmount
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const topViewModelAllRef = useRef({});

  const DataController = () => {
    const positionArray = useNavigationStore(
      (state) => state.navigationPositionArray,
    );
    useEffect(() => {
      latestNavigationPositionArrayRef.current = positionArray;
    }, [positionArray]);

    const navigationCameraPosition = useNavigationStore(
      (state) => state.navigationCameraPosition,
    );
    const navigationCameraLookingPosition = useNavigationStore(
      (state) => state.navigationCameraLookingPosition,
    );

    useEffect(() => {
      latestNavigationCameraPositionRef.current = navigationCameraPosition;
      latestNavigationCameraLookingPositionRef.current =
        navigationCameraLookingPosition;
    }, [navigationCameraPosition, navigationCameraLookingPosition]);

    const currentVenue = useNavigationStore((state) => state.currentVenue);
    const currentLcMapId = useNavigationStore((state) => state.currentLcMapId);
    useEffect(() => {
      currentLcMapIdRef.current = currentLcMapId;
      if (
        currentVenue == null ||
        currentVenue == "" ||
        currentLcMapId == null ||
        currentLcMapId == ""
      ) {
        return;
      }
      const mapSetting = getMapSettingByVenueCode(currentVenue);
      setMapSettingState(mapSetting);
      mapSettingRef.current = mapSetting;
      const venueModelConfig = mapSetting.modelConfig;
      const modelConfigMap = getModelConfigMapByLcMapId(
        venueModelConfig,
        currentLcMapId,
      );
      const altitude = modelConfigMap.altitude;
      targetAltitudeRef.current = altitude / venueModelConfig.scaleFactor;
    }, [currentVenue, currentLcMapId]);

    const navigationPercentagePositionDictionary = useNavigationStore(
      (state) => state.navigationPercentagePositionDictionary,
    );
    useEffect(() => {
      latestNavigationPercentagePositionDictionary.current =
        navigationPercentagePositionDictionary;
    }, [navigationPercentagePositionDictionary]);

    return <></>;
  };

  const ProgressController = () => {
    const progress = useNavigationStore((state) => state.progress);
    useEffect(() => {
      latestProgressRef.current = progress;
    }, [progress]);
    return <></>;
  };

  const WalkPathsContainer = () => {
    const app = useApp();
    const isPointsSetup = useRef<boolean>(false);
    const [points, setPoints] = useState<number[][] | null>(null);
    const lastUpdate = useRef(0);
    const UPDATE_INTERVAL = 1 / 5;
    const handleUpdate = useCallback((dt: number) => {
      lastUpdate.current += dt;
      if (lastUpdate.current >= UPDATE_INTERVAL && !isPointsSetup.current) {
        updateFrame();
        lastUpdate.current = 0;
      }
    }, []);
    const processPoints = useRef([]);
    const setupPoints = () => {
      if (
        !isPointsSetup.current &&
        latestNavigationPercentagePositionDictionary.current
      ) {
        processPoints.current = [];
        const entries = Object.entries(
          latestNavigationPercentagePositionDictionary.current,
        );
        for (let i = 0; i < entries.length; i++) {
          processPoints.current.push(entries[i][1]);
        }
        setPoints([...processPoints.current]);
        isPointsSetup.current = true;
      }
      else{
        requestAnimationFrame(setupPoints);
      }
    };
    //useAppEvent("update", handleUpdate);

    useEffect(()=>{
      if(app){
        requestAnimationFrame(setupPoints)
      }
    },[app])

    return points != null ? <WalkPaths points={points} /> : <></>;
  };

  const WalkPaths = ({ points }: { points: number[][] }) => {
    const segments = points.slice(0, -1).map((start, i) => {
      const end = points[i + 1];
      const length = Math.hypot(
        end[0] - start[0],
        end[1] - start[1],
        end[2] - start[2],
      );
      return (
        <EachWalkPath
          key={i}
          start={start}
          end={end}
          length={length}
          pathIndex={i + 1}
          pathLength={points.length - 1}
        />
      );
    });
    return (
      <>
        {segments}
        <ExtraWalkPath points={points} />
      </>
    );
  };

  const ExtraWalkPath = ({ points }: { points: number[][] }) => {
    const _startPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
    const _endPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
    const entityRef = useRef();
    useEffect(() => {
      const material = entityRef.current.render.meshInstances[0].material;
      material.emissiveMap = material.diffuseMap;
      material.emissiveIntensity = 1.0;
      material.useLighting = false;
      material.emissive.set(...hexToRgbArray("#FFFFFF")); // Red color (R,G,B) - 0 to 1 range
      material.update();
    }, []);

    const app = useApp();
    const lastUpdate = useRef(0);
    const UPDATE_INTERVAL = 1 / 5;
    const handleUpdate = useCallback((dt: number) => {
      lastUpdate.current += dt;
      if (lastUpdate.current >= UPDATE_INTERVAL) {
        lastUpdate.current = 0;
        updateFrame();
      }
    }, []);
    const updateFrame = () => {
      if (
        entityRef.current == null ||
        latestProgressRef.current == 0 ||
        latestProgressRef.current == 1
      ) {
        return;
      }
      const currentPosition = getInterpolatedNumberArray(
        latestNavigationPercentagePositionDictionary.current,
        latestProgressRef.current,
      );

      const startPosition = getFloorNumberArray(
        latestNavigationPercentagePositionDictionary.current,
        latestProgressRef.current,
      );
      _startPositionRef.current.set(
        startPosition[0],
        startPosition[1],
        startPosition[2],
      );
      _endPositionRef.current.set(
        currentPosition[0],
        currentPosition[1],
        currentPosition[2],
      );
      const length = Math.hypot(
        _endPositionRef.current.x - _startPositionRef.current.x,
        _endPositionRef.current.y - _startPositionRef.current.y,
        _endPositionRef.current.z - _startPositionRef.current.z,
      );
      entityRef.current.setPosition(_startPositionRef.current);
      entityRef.current.lookAt(_endPositionRef.current);
      entityRef.current.setLocalScale(0.5, 0.5, length);
      entityRef.current.translateLocal(0, 0, -length / 2);
      entityRef.current.enabled = true;
    };
    useAppEvent("update", handleUpdate);

    return (
      <Entity ref={entityRef}>
        <Render type="box" />
      </Entity>
    );
  };

  const EachWalkPath = ({ start, end, length, pathIndex, pathLength }) => {
    const app = useApp();
    const entityRef = useRef();

    const pathCorrespondingPercentage = useRef<number>(-1);

    useEffect(() => {
      const material = entityRef.current.render.meshInstances[0].material;
      material.emissiveMap = material.diffuseMap;
      material.emissiveIntensity = 1.0;
      material.useLighting = false;
      material.emissive.set(...hexToRgbArray("#FFFFFF"));
      material.update();
      entityRef.current.setPosition(new pc.Vec3(start[0], start[1], start[2]));
      entityRef.current.lookAt(new pc.Vec3(end[0], end[1], end[2]));
      entityRef.current.setLocalScale(0.5, 0.5, length + 0.2);
      entityRef.current.translateLocal(0, 0, -length / 2);
      entityRef.current.enabled = true;
    }, []);

    const lastUpdate = useRef(0);
    const UPDATE_INTERVAL = 1 / 2;
    const handleUpdate = useCallback((dt: number) => {
      lastUpdate.current += dt;
      if (lastUpdate.current >= UPDATE_INTERVAL) {
        lastUpdate.current = 0;
        updateFrame();
      }
    }, []);
    const updateFrame = () => {
      if (entityRef.current == null) {
        return;
      }
      if (pathCorrespondingPercentage.current == -1) {
        const entries = Object.entries(
          latestNavigationPercentagePositionDictionary.current,
        );
        for (let i = 0; i < entries.length; i++) {
          if (
            end[0] == entries[i][1][0] &&
            end[1] == entries[i][1][1] &&
            end[2] == entries[i][1][2]
          ) {
            pathCorrespondingPercentage.current = entries[i][0];
          }
        }
      }
      if (latestProgressRef.current > pathCorrespondingPercentage.current) {
        // entityRef.current.enabled = true;
        entityRef.current.render.show();
      } else {
        //entityRef.current.enabled = false;
        entityRef.current.render.hide();
      }
    };
    useAppEvent("update", handleUpdate);

    return (
      <Entity ref={entityRef}>
        <Render type="box" />
      </Entity>
    );
  };

  const VenueSogs = () => {
    const app = useApp();
    const baseUrl = getBaseUrl();
    const floorSogParentRef = useRef();
    const floorSogRefs = useRef({});
    const allFloorSog = mapSettingState.modelConfig.floor.map((floor, i) => {
      if (floor.sog) {
        return (
          <Fragment key={floor.lcMapId}>
            <Entity>
              <SogSplat
                url={baseUrl + mapSettingState.modelConfig.folderPath + floor.sog}
                lcMapId={floor.lcMapId}
              ></SogSplat>
            </Entity>
          </Fragment>
        );
      } else {
        return <Fragment key={floor.lcMapId}></Fragment>;
      }
    });

    return <Entity ref={floorSogParentRef}>{allFloorSog}</Entity>;
  };

  const FloorMeshs = () => {
    const app = useApp();
    const baseUrl = getBaseUrl();
    const floorMeshParentRef = useRef();
    const allFloorMesh = mapSettingState.modelConfig.floor.map((floor, i) => {
      if (floor.mesh) {
        return (
          <Entity key={floor.lcMapId}>
            <FloorMeshModel
              meshUrl={
                baseUrl + mapSettingState.modelConfig.folderPath + floor.mesh.url
              }
              meshPosition={floor.mesh.position}
              meshRotationAngle={floor.mesh.rotationAngle}
              meshScale={floor.mesh.scale}
              meshColor={floor.mesh.color ?? "#FFFFFF"}
              meshOpacity={floor.mesh.opacity ?? 1}
              floorIndex={floor.index}
            />
          </Entity>
        );
      } else {
        return <Fragment key={floor.lcMapId}></Fragment>;
      }
    });
    return <Entity ref={floorMeshParentRef}>{allFloorMesh}</Entity>;
  };

  const FloorMeshModel = ({
    floorIndex,
    meshUrl,
    meshPosition,
    meshRotationAngle,
    meshScale,
    meshColor,
    meshOpacity,
  }: {
    floorIndex: number;
    meshUrl: string;
    meshPosition: number[];
    meshRotationAngle: number[];
    meshScale: number[];
    meshColor: string;
    meshOpacity: number;
  }) => {
    const meshEntityRef = useRef();
    const { asset: meshAsset } = useModel(meshUrl);
    const color = hexToRgbArray(meshColor);

    const changeMaterial = (entity) => {
      if (entity == null) {
        return;
      }
      const renders = entity.findComponents("render");
      renders.forEach((render) => {
        if (render.meshInstances) {
          render.meshInstances.forEach((meshInstance) => {
            // const currentMaterial = meshInstance.material;
            // const material = currentMaterial.clone();
            const material = meshInstance.material;
            meshInstance.material = material;
            if (material) {
              material.emissiveMap = material.diffuseMap;
              material.emissiveIntensity = 1.0;
              material.emissive = new pc.Color(color[0], color[1], color[2]);
              material.useLighting = false;
              material.update();
            }
          });
        }
      });
    };
    useEffect(() => {
      if (meshAsset && meshEntityRef.current) {
        requestAnimationFrame(() => {
          changeMaterial(meshEntityRef.current);
        });
      }
    }, [meshAsset]);

    return (
      <Entity
        ref={meshEntityRef}
        position={[
          meshPosition[0] / mapSettingState.modelConfig.scaleFactor,
          meshPosition[1] / mapSettingState.modelConfig.scaleFactor,
          meshPosition[2] / mapSettingState.modelConfig.scaleFactor,
        ]}
        rotation={meshRotationAngle}
        scale={[
          meshScale[0] / mapSettingState.modelConfig.scaleFactor,
          meshScale[1] / mapSettingState.modelConfig.scaleFactor,
          meshScale[2] / mapSettingState.modelConfig.scaleFactor,
        ]}
      >
        <Render type="asset" asset={meshAsset} />
      </Entity>
    );
  };

  const SogSplat = ({
    url,
    lcMapId,
  }: {
    url: string;
    lcMapId: string;
  }) => {
    const app = useApp();
    const floorRef = useRef();

    const handleUpdate = useCallback((dt: number) => {
      if (
        currentLcMapIdRef.current != null &&
        floorRef.current != null
      ) {
        if (currentLcMapIdRef.current == lcMapId) {
          floorRef.current.enabled = true;
        } else {
          floorRef.current.enabled = false;
        }
      }
    }, []);
    useAppEvent("update", handleUpdate);

    const { asset } = useSplat(url);
    return (
      <Entity
        enabled={true}
        ref={floorRef}
        rotation={[180, 0, 0]}
        position={[0, 0, 0]}
        scale={[
          1 / mapSettingState.modelConfig.scaleFactor,
          1 / mapSettingState.modelConfig.scaleFactor,
          1 / mapSettingState.modelConfig.scaleFactor,
        ]}
      >
        <GSplat asset={asset} />
      </Entity>
    );
  };

  const MainObject = () => {
    const app = useApp();
    const mainObjectEntityRef = useRef();
    const cameraScriptRef = useRef();
    const cameraRef = useRef();
    const _finalCameraPositionRef = useRef<pc.Vec3>(new pc.Vec3(0, 0, 0));
    const _finalCameraLookingPositionRef = useRef<pc.Vec3>(
      new pc.Vec3(0, 0, 0),
    );

    let _navigationCameraPosition;
    let isNavigationCameraPositionUpdated;
    let _navigationCameraLookingPosition;
    let isNavigationCameraLookingPositionUpdated;

    const lastUpdate = useRef(0);
    const UPDATE_INTERVAL = 1 / 10;
    const handleUpdate = useCallback((dt: number) => {
      lastUpdate.current += dt;
      if (lastUpdate.current >= UPDATE_INTERVAL) {
        updateFrame();
        lastUpdate.current = 0;
      }
    }, []);
    const updateFrame = () => {
      if (!cameraScriptRef.current) {
        return;
      }
      isNavigationCameraPositionUpdated = false;
      if (
        _navigationCameraPosition !=
          latestNavigationCameraPositionRef.current &&
        (latestNavigationCameraPositionRef.current.x != 0 ||
          latestNavigationCameraPositionRef.current.y != 0 ||
          latestNavigationCameraPositionRef.current.z != 0)
      ) {
        _navigationCameraPosition = latestNavigationCameraPositionRef.current;
        isNavigationCameraPositionUpdated = true;
      }
      isNavigationCameraLookingPositionUpdated = false;
      if (
        _navigationCameraLookingPosition !=
          latestNavigationCameraLookingPositionRef.current &&
        (latestNavigationCameraLookingPositionRef.current.x != 0 ||
          latestNavigationCameraLookingPositionRef.current.y != 0 ||
          latestNavigationCameraLookingPositionRef.current.z != 0)
      ) {
        _navigationCameraLookingPosition =
          latestNavigationCameraLookingPositionRef.current;
        isNavigationCameraLookingPositionUpdated = true;
      }
      if (
        isNavigationCameraPositionUpdated ||
        isNavigationCameraLookingPositionUpdated
      ) {

        const mapAlign = findMapAlignWithLcMapId(
          mapSettingRef.current.floorAlignment,
          currentLcMapIdRef.current,
        );
        const scaleFactor = SCALE_BASE / mapAlign.scale;
        
        _finalCameraPositionRef.current.set(
          _navigationCameraPosition.x,
          // _navigationCameraPosition.y + 1000 / mapSettingState.modelConfig.scaleFactor,
          _navigationCameraPosition.y + 20 + (mapSettingRef.current.modelConfig.topAltitude / scaleFactor),
          _navigationCameraPosition.z,
        );
        _finalCameraLookingPositionRef.current.set(
          _navigationCameraPosition.x,
          _navigationCameraPosition.y,
          _navigationCameraPosition.z - 0.001,
        );
      }

      cameraScriptRef.current.reset(
        _finalCameraLookingPositionRef.current,
        _finalCameraPositionRef.current,
      );
    };
    useAppEvent("update", handleUpdate);

    useEffect(() => {
      const defaultCameraLookingPosition = new pc.Vec3(
        mapSettingRef.current.modelConfig.defaultPosition[0] / mapSettingRef.current.modelConfig.scaleFactor,
        mapSettingRef.current.modelConfig.defaultPosition[1] / mapSettingRef.current.modelConfig.scaleFactor,
        mapSettingRef.current.modelConfig.defaultPosition[2] / mapSettingRef.current.modelConfig.scaleFactor,
      );
      const defaultPosition = new pc.Vec3(
        mapSettingRef.current.modelConfig.defaultPosition[0] / mapSettingRef.current.modelConfig.scaleFactor,
        mapSettingRef.current.modelConfig.defaultPosition[1] / mapSettingRef.current.modelConfig.scaleFactor + 50,
        mapSettingRef.current.modelConfig.defaultPosition[2] / mapSettingRef.current.modelConfig.scaleFactor,
      );
      mainObjectEntityRef.current.setPosition(defaultPosition);
      cameraScriptRef.current.reset(
        defaultCameraLookingPosition,
        defaultPosition,
      );
    }, []);

    return (
      <Entity ref={mainObjectEntityRef}>
        <Entity>
          <Camera clearColor="#000000" frustumCulling={false} />
          <Script ref={cameraScriptRef} script={CameraControls} />
        </Entity>
      </Entity>
    );
  };

  const EachFloorTopViewModel = ({ lcMapId, url, floorSize, yAxis }) => {
    const app = useApp();
    const currentLcMapId = useNavigationStore((state) => state.currentLcMapId);
    const mapAlign = findMapAlignWithLcMapId(
      mapSettingState.floorAlignment,
      lcMapId,
    );
    const scaleFactor = SCALE_BASE / mapAlign.scale;

    let topviewPosition = [0, 0, 0];
    if (mapSettingState.alignmentSetting == "mesh_alignment") {
      const cu = new CoordinateUtil(mapAlign);
      const { x, y } = cu.latLngToXY([
        mapAlign.center.lng,
        mapAlign.center.lat,
      ]);
      topviewPosition = [-x / scaleFactor, yAxis / scaleFactor, y / scaleFactor];
    } else {
      topviewPosition = [
        -floorSize.width / 2 / scaleFactor,
        yAxis / scaleFactor,
        -floorSize.height / 2 / scaleFactor,
      ];
    }
    
    return (
      <Entity
        ref={(node) => {
          topViewModelAllRef.current[lcMapId] = node;
        }}
        position={topviewPosition}
        rotation={[0, 180, 0]}
        scale={[
          floorSize.width / scaleFactor,
          1,
          floorSize.width / scaleFactor,
        ]}
      >
        {
          currentLcMapId == lcMapId ? <GlbModel app={app} url={url} /> : <></>
        }
      </Entity>
    );
  };

  const FloorTopViewModels = () => {
    const floorAlignments = mapSettingState.floorAlignment;
    const app = useApp();
    const baseUrl = getBaseUrl();
    const floortopViewModelParentRef = useRef();
    const lastUpdate = useRef(0);
     let _previousLcMapId = "x";

    // const UPDATE_INTERVAL = 1 / 5;
    // const handleUpdate = useCallback((dt: number) => {
    //   lastUpdate.current += dt;
    //   if (lastUpdate.current >= UPDATE_INTERVAL) {
    //     if (_previousLcMapId != currentLcMapIdRef.current) {
    //       _previousLcMapId = currentLcMapIdRef.current;
    //       updateFrame();
    //     }
    //     lastUpdate.current = 0;
    //   }
    // }, []);
    // useAppEvent("update", handleUpdate);
    // const updateFrame = (dt: number) => {
    //   for (const [key, eachTopView] of Object.entries(
    //     topViewModelAllRef.current,
    //   )) {
    //     if (!eachTopView) {
    //       continue;
    //     }
    //     if (currentLcMapIdRef.current == key) {
    //       //eachTopView.parent.enabled = true;
    //       //eachTopView.parent.setLocalScale(1, 1, 1);
    //     } else {
    //       //eachTopView.parent.enabled = false;
    //       //eachTopView.parent.setLocalScale(0, 0, 0);
    //     }
    //   }
    // };
    const allFloorTopViewModels = mapSettingState.modelConfig.floor.map(
      (floor, i) => {
        if (floor.topViewModel) {
          const floorSetting = findMapAlignWithLcMapId(
            floorAlignments,
            floor.lcMapId
          )
          const floorSize = floorSetting.size;
          return (
            <Entity key={floor.lcMapId} enabled={false}>
              <EachFloorTopViewModel
                lcMapId={floor.lcMapId}
                url={
                  baseUrl +
                  mapSettingState.modelConfig.folderPath +
                  floor.topViewModel
                }
                floorSize={floorSize}
                yAxis={floor.yAxis}
              />
            </Entity>
          );
        } else {
          return <Fragment key={floor.lcMapId}></Fragment>;
        }
      },
    );

    return (
      <Entity ref={floortopViewModelParentRef}>{allFloorTopViewModels}</Entity>
    );
  };

  const PlaycanvasScene = () => {
    const app = useApp();
    return (
      <>
        {mapSettingState ? <FloorTopViewModels /> : <></>}
        <WalkPathsContainer />
        {mapSettingState ? <VenueSogs /> : <></>}
        <FloorMeshs />
        <Environment exposure={1.0} luminance={0.4} />
        <MainObject />
      </>
    );
  };

  const TopDownScene = () => {
    const isDisplayTopViewScene = useNavigationStore((state) => state.isDisplayTopViewScene);
    return (
      <Application
        usePhysics={false}
        maxDeltaTime={0.033}
        timeScale={1.0}
        graphicsDeviceOptions={{
          powerPreference: "low-power",
          alpha: false,
          antialias: false,
          preferWebGl2: true,
          stencil: false,
        }}
      >
        {mapSettingState != null &&
        isDisplayTopViewScene &&
        mapSettingState.modelConfig != null &&
        mapSettingState.modelConfig.scaleFactor > 0 ? (
          <PlaycanvasScene />
        ) : (
          <></>
        )}
      </Application>
    );
  };

  const DirectionLight = () => {
    const navigationAngle = useNavigationStore(
      (state) => state.navigationAngle,
    );
    return (
      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
        style={{ transform: `rotate(${navigationAngle + 180}deg)` }}
      >
        {/*<div className="fixed w-[60px] h-[50px] left-1/2 -translate-x-1/2 -translate-y-full bg-gradient-to-t from-[#FFFFFFDD] to-[#FFFFFF00] [clip-path:polygon(0_0,100%_0,60%_100%,40%_100%)]"></div>*/}
        <div className="fixed w-[80px] left-1/2 -translate-x-1/2 -translate-y-full">
          <img
            src="/assets/spotlight.png"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    );
  };

  const LogoDot = () => {
    const navigationAngle = useNavigationStore(
      (state) => state.navigationAngle,
    );
    return (
      <div style={{ transform: `rotate(${navigationAngle + 180}deg)` }}>
        <img
          src="/assets/icon_logo_dot.png"
          className="w-full h-full object-cover"
        />
      </div>
    );
  };

  console.log(
    "Navigation top view component rendered , if this keep showing, something wrong",
  );

  return (
    <div
      id="navi-top-view-parent"
      className={`relative hex-crop ${isMobile ? "mobile" : "desktop"}`}
      style={{
        width: `${HEX_WIDTH_INNER}px`,
        height: `${HEX_HEIGHT_INNER}px`,
      }}
    >
      <div
        className={`relative left-0 bottom-0 h-[100%] w-[100%] transform rotate-180 overflow-hidden`}
      >
        <div
          className={`absolute h-[100%] w-[100%] overflow-hidden bg-black`}
        />

        <div
          id="div-above-canvas"
          className="absolute left-0 bottom-0"
          style={{
            width: `${HEX_WIDTH_INNER}px`,
            height: `${HEX_HEIGHT_INNER}px`,
          }}
        >
          <DataController />
          <ProgressController />
          <TopDownScene />
        </div>

        <div className="absolute top-1/2 left-1/2 w-[20px] h-[20px] -translate-x-1/2 -translate-y-1/2">
          <DirectionLight />
          <LogoDot />
        </div>
      </div>
    </div>
  );
}
